import { useMemo, useState } from 'react'
import { FolderOpen, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { CarpetaItem } from './CarpetaItem'
import { useEstadisticasDetalladas, CATEGORIAS_DOCUMENTO } from '@/core/hooks/useDocumentos'
import type { CategoriaDocumento } from '@/types/database'
import { formatFileSize } from '@/types/documentos'

interface CarpetasSidebarProps {
  clienteId: string
  categoriaSeleccionada: CategoriaDocumento | null
  onCategoriaChange: (cat: CategoriaDocumento | null) => void
}

// Categorías principales a mostrar (excluye jurídicas por ahora)
const CATEGORIAS_VISIBLES = [
  'poderes',
  'balances',
  'ddjj',
  'acuse', // ORR-191: adjuntos de presentación fiscal (calendario)
  'vep',   // ORR-191
  'contratos',
  'constancias',
  'identificacion',
  'comprobantes',
  'otros',
] as const

export function CarpetasSidebar({
  clienteId,
  categoriaSeleccionada,
  onCategoriaChange,
}: CarpetasSidebarProps) {
  const { data: stats, isLoading } = useEstadisticasDetalladas(clienteId)
  const [showEmptyFolders, setShowEmptyFolders] = useState(false)

  // Crear mapa de stats para lookup rápido
  const statsMap = useMemo(() => {
    if (!stats?.categorias) return new Map()
    return new Map(stats.categorias.map(s => [s.categoria, s]))
  }, [stats])

  // Calcular totales
  const totalDocs = stats?.total ?? 0
  const totalSize = stats?.tamanioTotal ?? 0

  // Separar carpetas con y sin documentos
  const { carpetasConDocs, carpetasVacias } = useMemo(() => {
    const conDocs: typeof CATEGORIAS_VISIBLES[number][] = []
    const vacias: typeof CATEGORIAS_VISIBLES[number][] = []

    for (const cat of CATEGORIAS_VISIBLES) {
      const catStats = statsMap.get(cat)
      if (catStats && catStats.cantidad > 0) {
        conDocs.push(cat)
      } else {
        vacias.push(cat)
      }
    }

    return { carpetasConDocs: conDocs, carpetasVacias: vacias }
  }, [statsMap])

  if (isLoading) {
    return (
      <div className="w-full p-4 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header con total */}
      <div className="p-4 border-b">
        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          Carpetas
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {totalDocs} documentos · {formatFileSize(totalSize)}
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {/* Opción "Ver todos" */}
          <button
            type="button"
            onClick={() => onCategoriaChange(null)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
              categoriaSeleccionada === null
                ? 'bg-accent/10 border border-accent'
                : 'hover:bg-muted border border-transparent'
            }`}
          >
            <FolderOpen className="h-5 w-5 text-accent" />
            <div className="flex-1">
              <p className="font-medium">Todos los documentos</p>
              <p className="text-xs text-muted-foreground">
                {totalDocs} {totalDocs === 1 ? 'documento' : 'documentos'}
              </p>
            </div>
          </button>

          {/* Separador */}
          <div className="h-px bg-border my-2" />

          {/* Carpetas con documentos */}
          {carpetasConDocs.map((cat) => {
            const config = CATEGORIAS_DOCUMENTO[cat]
            const catStats = statsMap.get(cat)

            return (
              <CarpetaItem
                key={cat}
                categoria={cat}
                label={config?.label ?? cat}
                color={config?.color ?? 'bg-gray-100 text-gray-800'}
                cantidadDocs={catStats?.cantidad ?? 0}
                tamanoTotal={catStats?.tamanoTotal ?? 0}
                isSelected={categoriaSeleccionada === cat}
                onClick={() => onCategoriaChange(cat as CategoriaDocumento)}
              />
            )
          })}

          {/* Toggle para carpetas vacías */}
          {carpetasVacias.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowEmptyFolders(!showEmptyFolders)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {showEmptyFolders ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                <span>{carpetasVacias.length} carpetas vacías</span>
              </button>

              {/* Carpetas vacías (colapsables) */}
              {showEmptyFolders && (
                <div className="space-y-1 opacity-60">
                  {carpetasVacias.map((cat) => {
                    const config = CATEGORIAS_DOCUMENTO[cat]

                    return (
                      <CarpetaItem
                        key={cat}
                        categoria={cat}
                        label={config?.label ?? cat}
                        color={config?.color ?? 'bg-gray-100 text-gray-800'}
                        cantidadDocs={0}
                        tamanoTotal={0}
                        isSelected={categoriaSeleccionada === cat}
                        onClick={() => onCategoriaChange(cat as CategoriaDocumento)}
                      />
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
